import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import { compact } from "lodash";
import { COLORS } from "../../themes/Base";
import useSharedStyles from "../../styles/Shared";
import CopyIcon from "../../components/Icons/CopyIcon";
import CopyAllIcon from "../../components/Icons/CopyAllIcon";
import { stripQueryParams, copyToClipboard } from "../../utils/Formatters";

type Props = {
  open: boolean;
  onClose: () => void;
  supplier: ProjectSupplier | null;
  project: Project;
};

const UrlsModal: React.FC<Props> = ({ open, onClose, supplier, project }) => {
  if (!supplier) return null;
  const { classes } = useSharedStyles();
  const supplierFieldNames = supplier?.fields.map((f) => f.source.name) || [];

  const generateSurveyUrl = (project_supplier: ProjectSupplier) => {
    let survey_url = `${location.protocol}//${location.host}/response`;

    survey_url += survey_url.includes("?") ? "&" : "?";
    survey_url += `obsrv_pid=${project.id}&obsrv_sid=${project_supplier.id}&`;

    survey_url += compact(
      project_supplier.fields.map((field) => {
        if (field.source) {
          return `${field.source.name}=${field.source.value}`;
        } else if (field.name && field.value) {
          return `${field.name}=${field.value}`;
        } else {
          return "";
        }
      })
    ).join("&");

    return (
      <Box sx={{ mb: 3 }}>
        <Box sx={{ fontSize: 16 }}>Survey URL (For Supplier)</Box>
        <Box sx={{ color: "#67676C", fontSize: 13, mb: 1 }}>
          This link initiates the survey and should be provided to your survey supplier:
        </Box>
        <Box className={classes.urlBox}>
          <Box sx={{ flex: 1, wordBreak: "break-all" }}>{survey_url}</Box>
          <IconButton sx={{ ml: 1 }} onClick={() => void navigator.clipboard.writeText(survey_url)}>
            <CopyIcon />
          </IconButton>
        </Box>
      </Box>
    );
  };

  const generateSurveyRedirects = (project_supplier: ProjectSupplier) => {
    const respondent_key = project_supplier.respondent_key || "obsrv_id";
    const status_key = project_supplier.status_key || "obsrv_v";
    const baseUrl = `${location.protocol}//${location.host}/complete?${respondent_key}=${project_supplier.opening_tag || "{{"
      }${respondent_key}${project_supplier.closing_tag || "}}"}&${status_key}=`;

    const supplierFields = compact(
      project_supplier.fields
        .filter((field) => field.required || field.source?.required)
        .map((field) => {
          if (field.source) {
            return `${field.source.name}=${project_supplier.opening_tag || "{{"}${field.source.name
              }${project_supplier.closing_tag || "}}"}`;
          } else if (field.name && field.name) {
            return `${field.name}=${project_supplier.opening_tag || "{{"}${field.name}${project_supplier.closing_tag || "}}"
              }`;
          } else {
            return "";
          }
        })
    ).join("&");

    const endpoints = [
      {
        label: "Complete",
        value: `${baseUrl}${project.response_statuses.completed}&${supplierFields}`,
      },
      {
        label: "Screened Out",
        value: `${baseUrl}${project.response_statuses.screened_out}&${supplierFields}`,
      },
      {
        label: "Quality Termination",
        value: `${baseUrl}${project.response_statuses.terminated}&${supplierFields}`,
      },
      {
        label: "Quota Full",
        value: `${baseUrl}${project.response_statuses.quota_full}&${supplierFields}`,
      },
    ];

    return (
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box>
            <Box sx={{ fontSize: 16 }}>Survey Redirect Endpoints (For Survey Tool)</Box>
            <Box sx={{ color: "#67676C", fontSize: 13, mb: 1 }}>
              Place these links in your survey tool settings to handle participant outcomes:
            </Box>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
            <IconButton
              sx={{
                color: "#67676C",
                px: 2,
                fontSize: 14,
                borderRadius: "8px",
                background: "#F8F8F9",
              }}
              onClick={() => {
                const plainAll = endpoints
                  .map((e) => `${e.label}: ${stripQueryParams(e.value, supplierFieldNames)}`)
                  .join("\n");
                const htmlAll = endpoints
                  .map((e) => `<strong>${e.label}</strong>: ${stripQueryParams(e.value, supplierFieldNames)}`)
                  .join("<br>");
                void copyToClipboard(plainAll, htmlAll);
              }}
            >
              <CopyAllIcon /> Copy All
            </IconButton>
          </Box>
        </Box>

        {endpoints.map((endpoint) => (
          <Box
            key={endpoint.label}
            className={classes.urlBox}
            sx={{
              mb: 1,
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Box sx={{ fontSize: 14, fontWeight: 700, mb: 0.5 }}>{endpoint.label}</Box>
              <Box sx={{ flex: 1, wordBreak: "break-all" }}>{endpoint.value}</Box>
            </Box>
            <IconButton
              onClick={() => {
                const cleaned = stripQueryParams(endpoint.value, supplierFieldNames);
                copyToClipboard(
                  `${endpoint.label}: ${cleaned}`,
                  `<strong>${endpoint.label}</strong>: ${cleaned}`
                );
              }}
            >
              <CopyIcon />
            </IconButton>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { bgcolor: COLORS.background },
      }}>
      <DialogTitle>
        Project URLs – {supplier.source.name} {supplier.country_code && <span>{supplier.country_code}</span>}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, color: "#000" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {generateSurveyUrl(supplier)}
        {generateSurveyRedirects(supplier)}
      </DialogContent>
    </Dialog>
  );
};

export default UrlsModal;
